import {Injectable, inject} from '@angular/core';
import {TranslateLoader, TranslateService} from '@ngx-translate/core';
import {Observable, defer, forkJoin, map, of, tap} from 'rxjs';
import {FeatureTranslateLoader, TranslationBundle} from './feature-translate.loader';

@Injectable({providedIn: 'root'})
export class FeatureTranslationService {
    private readonly loader = inject(TranslateLoader);
    private readonly translate = inject(TranslateService);
    private readonly merged = new Set<string>();

    ensure(...bundles: TranslationBundle[]): Observable<void> {
        return defer(() => {
            const loader = this.loader;
            // Embedded/test consumers can provide their own complete dictionaries.
            if (!(loader instanceof FeatureTranslateLoader) || !bundles.length) return of(void 0);
            loader.activate(bundles);
            const current = this.translate.getCurrentLang() || 'en';
            const fallback = this.translate.getFallbackLang() || 'en';
            // Include in-flight and previously selected languages: ngx-translate caches each
            // language's original request, so switching back must also keep feature keys.
            const languages = new Set([...loader.requestedLanguages, current, fallback]);
            return forkJoin([...languages].map(lang => loader.loadFeatures(lang, bundles).pipe(
                tap(dictionary => {
                    const added = bundles.filter(bundle => loader.hasBundle(lang, bundle) && !this.merged.has(`${lang}:${bundle}`));
                    if (!added.length) return;
                    // setTranslation emits synchronously; pipes may re-enter ensure while another
                    // language is pending. Mark first and never re-emit an unchanged cached pack.
                    added.forEach(bundle => this.merged.add(`${lang}:${bundle}`));
                    this.translate.setTranslation(lang, dictionary, true);
                }),
            ))).pipe(map(() => {
                if (bundles.some(bundle => !loader.hasBundle(current, bundle) && !loader.hasBundle(fallback, bundle))) {
                    throw new Error('Feature translations unavailable');
                }
            }));
        });
    }
}
